/**
 * Construction des time_entries à insérer en base, à partir de l'état
 * de la grille employé (rows + indemnities + ventilations + projets).
 *
 * Fonction PURE extraite de `useTimesheetActions.saveToDatabase` — la
 * logique métier la plus critique du workflow de soumission :
 *
 *   - Séparation hours_normal / hours_saturday / hours_major selon le
 *     jour de la semaine (lun-ven / sam / dim)
 *   - Résolution de la zone (savedZone > projet > null) avec override
 *     à null si GD
 *   - Reset indemnités transport/trajet en GD
 *   - Fallback description pour les "divers:" sub-projects
 *   - Détection et mapping des sous-projets "divers:..." vers leur
 *     project_id parent
 *   - Ventilation BE (étude/suivi/mise_en_service/chantier) regroupée
 *     par entry pour insertion après coup
 *   - Filtrage des jours verrouillés et des jours de formation
 *   - Ajout automatique des entries FORMATION (7h hours_normal) sur
 *     les trainingDays
 */

import type { Project, DiversSubProject } from '../types';
import type {
  GridRow,
  DailyIndemnity,
  Ventilation,
  TimeEntryInsert,
} from '../pages/timesheet/types';

export interface BuildEntriesInput {
  rows: GridRow[];
  indemnities: Record<string, DailyIndemnity>;
  ventilations: Record<string, Ventilation>;
  trainingDays: string[];
  trainingProjectId: string;
  projects: Project[];
  diversSubProjects: DiversSubProject[];
  sheetId: string;
  isBE: boolean;
  /** Retourne true si un jour est verrouillé (déjà soumis, feuille validée...) */
  isLockedForDay: (day: Date) => boolean;
  /** Retourne true si un jour est hors du mois de saisie actif (semaine à cheval) */
  isOutsideEntryMonth?: (day: Date) => boolean;
}

export interface BuildEntriesResult {
  entriesToInsert: TimeEntryInsert[];
  /** Map `${actualProjectId}_${work_date}` → Ventilation, à utiliser après
   * l'insertion des entries pour associer les ventilations aux bons IDs. */
  ventilationMap: Record<string, Ventilation>;
}

/**
 * Construit la liste des time_entries à insérer + la map de ventilations
 * en attendant les IDs post-insert.
 *
 * Règles métier (miroir du code d'origine de saveToDatabase) :
 * - Jour lun-ven → hours_normal, sam → hours_saturday, dim → hours_major
 * - Jour GD → travel_zone=null, indemnity_transport=0, indemnity_trajet=0,
 *   gd_amount et gd_travel_hours recopiés (prio indemnity > projet)
 * - Hors GD : travel_zone hérité de la row.zones[ds] (saisie utilisateur),
 *   fallback vers la zone du projet si absent
 * - Sous-projet "divers:xxx" → le project_id final = parent_project_id
 *   + description par défaut = row.projectName si vide
 * - Jours de formation (trainingDays) → 1 entry FORMATION à 7h par jour
 *   non verrouillé, sur trainingProjectId
 * - Jours verrouillés skipés (soumis sur un autre mois, feuille validée...)
 * - Jours hors mois actif skipés (semaine à cheval, isOutsideEntryMonth)
 */
export function buildTimeEntriesToInsert(
  input: BuildEntriesInput,
): BuildEntriesResult {
  const {
    rows, indemnities, ventilations,
    trainingDays, trainingProjectId,
    projects, diversSubProjects,
    sheetId, isBE, isLockedForDay,
    isOutsideEntryMonth,
  } = input;

  const entriesToInsert: TimeEntryInsert[] = [];
  const ventilationMap: Record<string, Ventilation> = {};

  for (const row of rows) {
    const isDiversSubProject = row.projectId.startsWith('divers:');
    let actualProjectId = row.projectId;
    let diversSubProjectId: string | null = null;
    let projectZone: string | number | null = null;

    if (isDiversSubProject) {
      const subId = row.projectId.replace('divers:', '');
      diversSubProjectId = subId;  // posé sur chaque entry issue de ce row
      const subProject = diversSubProjects.find(s => s.id === subId);
      if (subProject) {
        actualProjectId = subProject.parent_project_id;
        const parentProject = projects.find(p => p.id === subProject.parent_project_id);
        projectZone = (subProject.zone ?? parentProject?.zone) ?? null;
      }
    } else {
      const projectData = projects.find(p => p.id === row.projectId);
      projectZone = projectData?.zone ?? null;
    }

    for (const [ds, hours] of Object.entries(row.hours)) {
      if (trainingDays.includes(ds)) continue;

      const dateObj = new Date(ds);
      if (isLockedForDay(dateObj)) continue;
      if (isOutsideEntryMonth && isOutsideEntryMonth(dateObj)) continue;

      const comment = row.descriptions[ds] || null;
      const dayOfWeek = dateObj.getDay(); // 0=dim, 6=sam
      const dailyIndemnity = indemnities[ds] || {
        meals: 0, transport: 0, trajet: 0, is_grand_deplacement: false,
      };

      const projectData = projects.find(
        p => p.id === (isDiversSubProject ? actualProjectId : row.projectId),
      );

      const isGD = dailyIndemnity.is_grand_deplacement === true;

      const gdAmountToSave = dailyIndemnity.gd_amount ?? projectData?.gd_amount ?? null;
      const gdTravelHoursToSave = dailyIndemnity.gd_travel_hours ?? projectData?.gd_travel_hours ?? null;

      const savedZone = row.zones[ds];
      const zoneToSave = isGD
        ? null
        : (savedZone !== undefined && savedZone !== null ? savedZone : projectZone);

      let finalDescription = comment;
      if (isDiversSubProject && !finalDescription) {
        finalDescription = row.projectName;
      }

      entriesToInsert.push({
        sheet_id: sheetId,
        project_id: actualProjectId,
        work_date: ds,
        hours_normal: (dayOfWeek >= 1 && dayOfWeek <= 5) ? hours : 0,
        hours_saturday: (dayOfWeek === 6) ? hours : 0,
        hours_major: (dayOfWeek === 0) ? hours : 0,
        meals_count: dailyIndemnity.meals,
        travel_zone: zoneToSave,
        indemnity_transport: isGD ? 0 : dailyIndemnity.transport,
        indemnity_trajet: isGD ? 0 : dailyIndemnity.trajet,
        description: finalDescription,
        is_grand_deplacement: isGD,
        gd_amount: isGD ? gdAmountToSave : null,
        gd_travel_hours: isGD ? gdTravelHoursToSave : null,
        divers_sub_project_id: diversSubProjectId,
      });

      if (isBE) {
        // Inclure sub_id dans la clé ventilation pour distinguer 2 sub-projects
        // même parent même jour côté ventilation.
        const origKey = `${row.projectId}_${ds}`;
        const dbKey = `${actualProjectId}|${diversSubProjectId ?? ''}|${ds}`;
        if (ventilations[origKey]) ventilationMap[dbKey] = ventilations[origKey];
      }
    }
  }

  // Entries FORMATION (7h forfaitaires les jours marqués)
  if (trainingDays.length > 0 && trainingProjectId) {
    for (const ds of trainingDays) {
      const trainingDate = new Date(ds);
      if (isLockedForDay(trainingDate)) continue;
      if (isOutsideEntryMonth && isOutsideEntryMonth(trainingDate)) continue;

      entriesToInsert.push({
        sheet_id: sheetId,
        project_id: trainingProjectId,
        work_date: ds,
        hours_normal: 7,
        hours_saturday: 0,
        hours_major: 0,
        meals_count: 0,
        travel_zone: null,
        indemnity_transport: 0,
        indemnity_trajet: 0,
      });
    }
  }

  // ─── Plus d'agrégation depuis l'ajout de divers_sub_project_id ─────────
  // L'ancienne version (avant migration 20260519) agrégeait les entries
  // partageant (sheet_id, work_date, project_id) parce que la contrainte
  // unique l'imposait. Désormais la contrainte est sur (sheet_id, work_date,
  // project_id, COALESCE(divers_sub_project_id, nil)), donc 2 sous-projets
  // divers du même parent le même jour produisent légitimement 2 entries
  // distinctes — la corruption originelle (incident CORDIER S19) est
  // structurellement impossible. Les rows sont stockées telles quelles.

  return { entriesToInsert, ventilationMap };
}
